/**
 * Rendu de la forteresse ennemie d'une zone (phase D) : mêmes boîtes que la collision, acier rouge sombre à
 * panneaux (détail de surface des docks), bandes lumineuses hostiles, socles des tourelles, cœur pulsant au
 * sommet relié à la tour, mâts balisés. Les tourelles elles-mêmes sont des vaisseaux (rendu des ennemis).
 */
import { Color3, MeshBuilder, PBRMaterial, Quaternion, StandardMaterial, TransformNode, Vector3, type Mesh, type Scene } from '@babylonjs/core';
import type { ArtData, FortressData } from '../data';
import { markSolid } from './postfx';
import { SurfaceDetailPlugin, type SurfaceDetailSet } from './surfaceDetail';

const HOSTILE = new Color3(1, 0.16, 0.05);

export class FortressView {
  readonly root: TransformNode;
  private readonly beacons: Mesh[] = [];
  private readonly core: Mesh;
  private readonly coreMaterial: StandardMaterial;
  private time = 0;

  constructor(scene: Scene, name: string, private readonly data: FortressData, art: ArtData, detail: SurfaceDetailSet) {
    this.root = new TransformNode(`fortress-${name}`, scene);
    this.root.position.set(...data.position);
    this.root.rotationQuaternion = new Quaternion(...data.rotation);
    const world = this.root.computeWorldMatrix(true);

    const steel = (id: string, albedo: [number, number, number], metallic: number, detailScale: number) => {
      const m = new PBRMaterial(`fortress-${name}-${id}`, scene);
      m.albedoColor = new Color3(...albedo);
      m.metallic = metallic;
      m.roughness = 0.5;
      m.environmentIntensity = 0.15;
      m.maxSimultaneousLights = 8;
      const d = art.dockDetail;
      // Pas de volume « hangar » : panneaux de coque partout.
      const plugin = new SurfaceDetailPlugin(m, detail, { hullTile: d.hullTile * 1.4, hangarTile: d.hangarTile, albedo: d.albedo * detailScale, normal: d.normal });
      world.invertToRef(plugin.invWorld);
      return m;
    };
    const hull = steel('hull', [0.14, 0.07, 0.065], 0.65, 0.35);
    const trim = steel('trim', [0.05, 0.035, 0.035], 0.7, 0.15);
    const glow = (id: string, color: Color3) => {
      const m = new StandardMaterial(`fortress-${name}-${id}`, scene);
      m.disableLighting = true;
      m.emissiveColor = color;
      return m;
    };
    const strip = glow('strip', HOSTILE.scale(0.8));
    const add = (id: string, size: [number, number, number], at: [number, number, number], material: PBRMaterial | StandardMaterial) => {
      const box = MeshBuilder.CreateBox(`fortress-${name}-${id}`, { width: size[0], height: size[1], depth: size[2] }, scene);
      box.position.set(...at);
      box.parent = this.root;
      box.material = material;
      box.isPickable = false;
      // Coque (PBR) : objet solide pour les ombres ; bandes lumineuses exclues.
      if (material instanceof PBRMaterial) markSolid(box);
      return box;
    };

    for (const part of data.parts) {
      const [hx, hy, hz] = part.half as [number, number, number];
      const [cx, cy, cz] = part.center as [number, number, number];
      add(part.id, [hx * 2, hy * 2, hz * 2], [cx, cy, cz], hull);
      // Bandes lumineuses à mi-hauteur sur les grandes faces latérales, et arêtes sombres en saillie.
      if (hy < 12) continue;
      const along = hx >= hz ? 'x' : 'z';
      const length = (along === 'x' ? hx : hz) * 2 - 6;
      for (const side of [-1, 1]) {
        if (along === 'x') {
          add(`${part.id}-strip-${side}`, [length, 0.6, 0.2], [cx, cy, cz + side * (hz + 0.12)], strip);
          add(`${part.id}-edge-${side}`, [length + 4, 1.4, 1.4], [cx, cy + hy, cz + side * hz], trim);
        } else {
          add(`${part.id}-strip-${side}`, [0.2, 0.6, length], [cx + side * (hx + 0.12), cy, cz], strip);
          add(`${part.id}-edge-${side}`, [1.4, 1.4, length + 4], [cx + side * hx, cy + hy, cz], trim);
        }
      }
    }

    // Emplacements des tourelles : liseré rouge sur la face d'appui (5 m sous le centre de la tourelle), autour du
    // socle du modèle (turret_base, ≈ 11 m de diamètre, rendu avec la tourelle).
    const ringMat = glow('turret-ring', HOSTILE);
    for (const slot of data.turrets) {
      const normal = new Vector3(...slot.normal).normalize();
      const ring = MeshBuilder.CreateTorus(`fortress-${name}-pedestal-ring-${slot.id}`, { diameter: 12, thickness: 0.35, tessellation: 40 }, scene);
      ring.rotationQuaternion = alignY(normal);
      ring.position = new Vector3(...slot.position).add(normal.scale(-4.95));
      ring.parent = this.root;
      ring.isPickable = false;
      ring.material = ringMat;
    }

    // Cœur au sommet de la tour, relié par un mât ; anneau de confinement.
    const tower = data.parts.reduce((a, p) => (p.center[1]! + p.half[1]! > a.center[1]! + a.half[1]! ? p : a));
    const towerTop = tower.center[1]! + tower.half[1]!;
    const c = data.core;
    const mast = MeshBuilder.CreateCylinder(`fortress-${name}-mast`, { height: c.center[1]! - towerTop, diameterTop: 2, diameterBottom: 6, tessellation: 12 }, scene);
    mast.position.set(c.center[0]!, (towerTop + c.center[1]!) / 2, c.center[2]!);
    mast.parent = this.root;
    mast.material = trim;
    mast.isPickable = false;
    this.coreMaterial = glow('core', new Color3(1, 0.3, 0.08));
    this.core = MeshBuilder.CreateSphere(`fortress-${name}-core`, { diameter: c.radius * 2, segments: 24 }, scene);
    this.core.position.set(...c.center);
    this.core.parent = this.root;
    this.core.material = this.coreMaterial;
    this.core.isPickable = false;
    const halo = MeshBuilder.CreateTorus(`fortress-${name}-core-ring`, { diameter: c.radius * 3.2, thickness: 0.9, tessellation: 48 }, scene);
    halo.position.set(...c.center);
    halo.rotation.x = 0.35;
    halo.parent = this.root;
    halo.material = trim;
    halo.isPickable = false;

    // Mâts balisés aux extrémités des bras.
    const beacon = glow('beacon', HOSTILE);
    const outer = data.parts.filter((p) => Math.hypot(p.center[0]!, p.center[2]!) > 150);
    for (const pod of outer) {
      const top = pod.center[1]! + pod.half[1]!;
      const pole = MeshBuilder.CreateCylinder(`fortress-${name}-pole-${pod.id}`, { height: 18, diameter: 0.9, tessellation: 8 }, scene);
      pole.position.set(pod.center[0]!, top + 9, pod.center[2]!);
      pole.parent = this.root;
      pole.material = trim;
      pole.isPickable = false;
      const light = MeshBuilder.CreateSphere(`fortress-${name}-beacon-${pod.id}`, { diameter: 2.2, segments: 8 }, scene);
      light.position.set(pod.center[0]!, top + 19, pod.center[2]!);
      light.parent = this.root;
      light.material = beacon;
      light.isPickable = false;
      this.beacons.push(light);
    }
  }

  setEnabled(enabled: boolean): void {
    this.root.setEnabled(enabled);
  }

  update(dt: number): void {
    if (!this.root.isEnabled()) return;
    this.time += dt;
    const pulse = 0.75 + 0.25 * Math.sin(this.time * 2.2);
    this.coreMaterial.emissiveColor.set(pulse, 0.3 * pulse, 0.08 * pulse);
    this.core.scaling.setAll(0.94 + 0.06 * pulse);
    const blink = Math.sin(this.time * 2.6) > 0.1;
    for (const light of this.beacons) light.setEnabled(blink);
  }

  /** Point au-dessus du cœur (marqueur HUD). */
  marker(): Vector3 {
    return Vector3.TransformCoordinates(new Vector3(...this.data.core.center).add(new Vector3(0, this.data.core.radius + 6, 0)), this.root.computeWorldMatrix(true));
  }
}

/** Rotation qui amène l'axe Y local sur `normal`. */
function alignY(normal: Vector3): Quaternion {
  const up = Vector3.Up();
  const axis = Vector3.Cross(up, normal);
  const dot = Vector3.Dot(up, normal);
  if (axis.lengthSquared() < 1e-8) return dot > 0 ? Quaternion.Identity() : Quaternion.RotationAxis(Vector3.Right(), Math.PI);
  return Quaternion.RotationAxis(axis.normalize(), Math.acos(Math.max(-1, Math.min(1, dot))));
}
